import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Callout,
  DataList,
  Dialog,
  Flex,
  Text,
} from '@/components/ui';
import { IconCloudUpload, IconX } from '@tabler/icons-react';
import { UploadStatistics } from '../types';

interface UploadConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  statistics: UploadStatistics;
}

const formatFileCount = (
  count: number,
  singular: string,
  plural: string
): string => {
  const text = count === 1 ? singular : plural;
  return `${count} ${text}`;
};

export const UploadConfirmationModal: React.FC<
  UploadConfirmationModalProps
> = ({ isOpen, onClose, onConfirm, statistics }) => {
  const hasReplacedFiles = statistics.replacedFiles > 0;
  const hasSkippedFiles =
    statistics.skippedNotApproved > 0 ||
    statistics.skippedDuplicates > 0 ||
    statistics.skippedAlreadyExist > 0;

  const uploadItems = useMemo(() => {
    const items = [
      {
        label: <Text size="2xsmall">Total files:</Text>,
        value: (
          <Text size="2xsmall" weight="medium">
            {statistics.filesToUpload}
          </Text>
        ),
      },
    ];

    if (statistics.newFiles > 0) {
      items.push({
        label: (
          <Text size="2xsmall" color="success">
            New files:
          </Text>
        ),
        value: (
          <Text size="2xsmall" weight="medium" color="success">
            {statistics.newFiles}
          </Text>
        ),
      });
    }

    if (statistics.replacedFiles > 0) {
      items.push({
        label: (
          <Text size="2xsmall" color="warning">
            Files to replace:
          </Text>
        ),
        value: (
          <Text size="2xsmall" weight="medium" color="warning">
            {statistics.replacedFiles}
          </Text>
        ),
      });
    }

    return items;
  }, [statistics.filesToUpload, statistics.newFiles, statistics.replacedFiles]);

  const skippedItems = useMemo(() => {
    const items = [];

    if (statistics.skippedNotApproved > 0) {
      items.push({
        label: (
          <Text size="2xsmall" color="error" weight="medium">
            Not approved:
          </Text>
        ),
        value: (
          <Text size="2xsmall" color="error" weight="medium">
            {statistics.skippedNotApproved}
          </Text>
        ),
      });
    }

    if (statistics.skippedDuplicates > 0) {
      items.push({
        label: (
          <Text size="2xsmall" color="error" weight="medium">
            Duplicates:
          </Text>
        ),
        value: (
          <Text size="2xsmall" color="error" weight="medium">
            {statistics.skippedDuplicates}
          </Text>
        ),
      });
    }

    if (statistics.skippedAlreadyExist > 0) {
      items.push({
        label: <Text size="2xsmall">Already exist:</Text>,
        value: <Text size="2xsmall">{statistics.skippedAlreadyExist}</Text>,
      });
    }

    return items;
  }, [
    statistics.skippedNotApproved,
    statistics.skippedDuplicates,
    statistics.skippedAlreadyExist,
  ]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open: boolean) => !open && onClose()}
    >
      <Dialog.Content className="sm:max-w-[500px]">
        <Dialog.Header
          startSlot={
            <Avatar radius="medium">
              <IconCloudUpload style={{ color: 'var(--blue-9)' }} />
            </Avatar>
          }
          title="Add to Queue & Start Upload"
          subtitle="Files will be added to upload queue and upload will start
              immediately"
        />

        <Dialog.Body>
          <Flex direction="column" gap="3">
            {/* Upload Summary */}
            <Box>
              <Text size="xsmall" weight="medium" mb="2">
                Files to Upload
              </Text>
              <DataList
                alignValues="end"
                items={uploadItems}
                size="small"
                orientation="horizontal"
              />
            </Box>

            {/* Skipped Files */}
            {hasSkippedFiles && (
              <Box>
                <Text size="xsmall" weight="medium" mb="2">
                  Skipped Files
                </Text>
                <DataList
                  alignValues="end"
                  items={skippedItems}
                  size="small"
                  orientation="horizontal"
                />
              </Box>
            )}

            {/* Warning for file replacements */}
            {hasReplacedFiles && (
              <Callout
                size="small"
                icon={<IconX />}
                color="warning"
                variant="surface"
                title=" Warning"
              >
                <Text size="3xsmall" color="warning">
                  {formatFileCount(
                    statistics.replacedFiles,
                    'file will',
                    'files will'
                  )}{' '}
                  overwrite existing files on the server. This action cannot be
                  undone.
                </Text>
              </Callout>
            )}

            <Text size="2xsmall" color="textSecondary" mt="2">
              Files will be added to the upload queue and the upload process
              will begin immediately. You can monitor the progress in the upload
              widget.
            </Text>
          </Flex>
        </Dialog.Body>

        <Dialog.Footer>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="classic"
            color="primary"
            onClick={onConfirm}
            startIcon={<IconCloudUpload />}
          >
            Add to Queue & Start Upload
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
